import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  AlertTitle,
  Tooltip,
} from '@mui/material';
import {
  Bar,
  Doughnut,
  Line,
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
  CalendarMonth,
  AutorenewRounded,
  Error as ErrorIcon,
  CheckCircle,
  Build as BuildIcon,
  Cancel,
  WarningAmberRounded,
  TrendingUp,
} from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import { useAuth } from './AuthContext';

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  ChartDataLabels
);

interface PipelineError {
  id: string;
  pipelineId: string;
  appName: string;
  submitter: string;
  timestamp: string;
  status: string;
  phase?: string;
  errorType: 'failed' | 'timeout' | 'stuck' | 'cancelled';
  errorDescription: string;
  errorCode?: string | null;
  platform?: string;
  version?: string;
  pipelineUrl?: string;
}

interface BuildAnalyticsDashboardProps {
  isActive: boolean;
}

const BuildAnalyticsDashboard: React.FC<BuildAnalyticsDashboardProps> = ({ isActive }) => {
  const { dashboardData, fetchDashboardData, isLoadingAppData } = useAuth();

  // State
  const [allPipelineErrors, setAllPipelineErrors] = useState<PipelineError[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const initialFetchTriggeredRef = useRef(false);

  // Get ALL builds from cached data
  const allBuilds = useMemo(() => {
    return dashboardData?.recentBuilds || [];
  }, [dashboardData]);

  // Derive errors from ALL builds
  useEffect(() => {
    if (!allBuilds.length) return;

    const errorsOnly: PipelineError[] = allBuilds
      .filter((b: any) => {
        const s = (b.status || b.pipelineStatus || '').toLowerCase();
        return s.includes('fail') || s.includes('cancel') || s.includes('stuck') || s.includes('timeout');
      })
      .map((b: any) => {
        const s = (b.status || '').toLowerCase();
        let errorType: PipelineError['errorType'] = 'failed';
        if (s.includes('stuck')) errorType = 'stuck';
        else if (s.includes('timeout')) errorType = 'timeout';
        else if (s.includes('cancel')) errorType = 'cancelled';

        // Schoolbuzz CI extracts a precise reason via error_patterns.conf and
        // the parent-app-worker persists it on the build (failureReason) and
        // the failing phase (errorMessage). Prefer the build-level reason —
        // that's what update_build_status posts at the very end of the trap
        // chain — and fall back through the phase-level fields.
        const description =
          b.failureReason ||
          b.errorMessage ||
          b.phaseStatus ||
          'No detailed diagnostic logs available';

        return {
          id: String(b.id || b.pipelineId || Math.random()),
          pipelineId: String(b.pipelineId || 'N/A'),
          appName: b.composerHost || b.domain || 'Unknown',
          submitter: b.submitter || b.user || 'N/A',
          timestamp: b.dateTimeStamp || b.updatedAt || new Date().toISOString(),
          status: b.pipelineStatus || b.status || 'Failed',
          phase: b.failedPhase || b.phase || 'Unknown',
          errorType,
          platform: b.osSpecification || b.platform || '',
          version: b.version || '',
          pipelineUrl: b.pipelineUrl || '',
          errorDescription: description,
          errorCode: b.errorCode || null
        };
      });

    setAllPipelineErrors(errorsOnly);
  }, [allBuilds]);

  // Notify backend when this page opens/closes so the bulk refresher only runs then
  useEffect(() => {
    const token = localStorage.getItem('token');
    const signal = (open: boolean) => {
      fetch('/api/appdata/build-analytics-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ open })
      }).catch(() => {});
    };
    signal(isActive);
    return () => { if (isActive) signal(false); };
  }, [isActive]);

  // Initial fetch and Poll Loop
  useEffect(() => {
    if (!isActive) return;

    if (!initialFetchTriggeredRef.current) {
      initialFetchTriggeredRef.current = true;
      fetchDashboardData();
    }

    const interval = setInterval(() => {
      fetchDashboardData();
    }, 35000 + Math.random() * 5000);

    return () => clearInterval(interval);
  }, [isActive, fetchDashboardData]);

  // Chart Data Calculations
  const buildStats = useMemo(() => {
    const total = allBuilds.length;
    const success = allBuilds.filter((b: any) => (b.status || b.pipelineStatus || '').toLowerCase() === 'success').length;
    const failed = allBuilds.filter((b: any) => {
      const s = (b.status || b.pipelineStatus || '').toLowerCase();
      return s.includes('fail') && !s.includes('cancel');
    }).length;
    const canceled = allBuilds.filter((b: any) => (b.status || b.pipelineStatus || '').toLowerCase().includes('cancel')).length;
    const running = total - success - failed - canceled;

    return { total, success, failed, canceled, running };
  }, [allBuilds]);

  // Daily build counts (last 7 days)
  const dailyBuilds = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const date = new Date(Date.now() - (6 - i) * 86400000);
      const count = allBuilds.filter((b: any) => {
        const buildDate = new Date(b.dateTimeStamp || b.updatedAt);
        return isSameDay(buildDate, date);
      }).length;
      return {
        date: format(date, 'MMM dd'),
        count
      };
    });
  }, [allBuilds]);

  // Phase failure analysis
  const phaseFailures = useMemo(() => {
    const phases = ['npm_build', 'update_configs', 'fastlane', 'code_signing', 'upload', 'finalize'];
    const failedBuilds = allBuilds.filter((b: any) => {
      const s = (b.status || b.pipelineStatus || '').toLowerCase();
      return s.includes('fail');
    });

    return phases.map(phase => ({
      phase: phase.replace(/_/g, ' ').toUpperCase(),
      count: failedBuilds.filter((b: any) => (b.phase || '').toLowerCase() === phase).length
    })).filter(p => p.count > 0);
  }, [allBuilds]);

  // Builds by user
  const userBuilds = useMemo(() => {
    const userCounts: Record<string, number> = {};
    allBuilds.forEach((b: any) => {
      const user = b.submitter || b.user || 'Unknown';
      userCounts[user] = (userCounts[user] || 0) + 1;
    });
    return Object.entries(userCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [allBuilds]);

  // Top 10 affected apps
  const topFailedApps = useMemo(() => {
    const appFailures: Record<string, number> = {};
    allPipelineErrors.forEach(error => {
      appFailures[error.appName] = (appFailures[error.appName] || 0) + 1;
    });
    return Object.entries(appFailures)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [allPipelineErrors]);

  // -------------------- Success rate over time + heatmap calendar --------------------
  // We bucket every build (not just failures) by UTC date so the trend reflects
  // actual deploy throughput, not just the dates that happened to fail.
  const dayBuckets = useMemo(() => {
    const buckets = new Map<string, { total: number; success: number; failed: number; cancelled: number; running: number }>();
    allBuilds.forEach((b: any) => {
      const ts = b.dateTimeStamp || b.updatedAt || b.createdAt;
      if (!ts) return;
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const bucket = buckets.get(key) ?? { total: 0, success: 0, failed: 0, cancelled: 0, running: 0 };
      bucket.total++;
      const s = (b.status || b.pipelineStatus || '').toLowerCase();
      if (/success/.test(s)) bucket.success++;
      else if (/cancel/.test(s)) bucket.cancelled++;
      else if (/fail/.test(s)) bucket.failed++;
      else bucket.running++;
      buckets.set(key, bucket);
    });
    return buckets;
  }, [allBuilds]);

  // 30-day success-rate trend. Rate counts only terminal builds (success +
  // failed) so days with everything still running render as a gap, not 0%.
  const successRateTrend = useMemo(() => {
    const out: Array<{ date: string; rate: number | null; total: number }> = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const bucket = dayBuckets.get(key);
      const total = bucket?.total ?? 0;
      const terminal = bucket ? (bucket.success + bucket.failed) : 0;
      const rate = terminal > 0 ? ((bucket!.success / terminal) * 100) : null;
      out.push({ date: format(d, 'MMM dd'), rate, total });
    }
    return out;
  }, [dayBuckets]);

  // Calendar grid for the current visible month — week rows × 7 day columns.
  const heatmapMonth = useMemo(() => {
    const ref = new Date();
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startWeekday = firstDay.getDay(); // 0 = Sun
    const cells: Array<{ date: Date; key: string; bucket?: any; failureRate: number | null } | null> = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const d = new Date(year, month, day);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const bucket = dayBuckets.get(key);
      const failureRate = bucket && bucket.total > 0
        ? ((bucket.failed + bucket.cancelled) / bucket.total) * 100
        : null;
      cells.push({ date: d, key, bucket, failureRate });
    }
    return { label: format(firstDay, 'MMMM yyyy'), cells };
  }, [dayBuckets]);

  // -------------------- Flaky-build detection --------------------
  // An app is flagged "flaky" if within the recent build window we see a
  // failure followed by a success on the same composer host AND same version
  // — i.e. the code didn't change but the pipeline outcome flipped.
  const flakyApps = useMemo(() => {
    const byApp = new Map<string, any[]>();
    allBuilds.forEach((b: any) => {
      const key = b.composerHost || b.domain || 'unknown';
      const arr = byApp.get(key) ?? [];
      arr.push(b);
      byApp.set(key, arr);
    });
    const flaky: Array<{ appName: string; flipCount: number; lastFlipAt: string | null; sample: any }> = [];
    byApp.forEach((builds, appName) => {
      // Oldest → newest
      const ordered = [...builds].sort((a, b) => {
        const ta = new Date(a.dateTimeStamp || a.updatedAt || 0).getTime();
        const tb = new Date(b.dateTimeStamp || b.updatedAt || 0).getTime();
        return ta - tb;
      });
      let flipCount = 0;
      let lastFlipAt: string | null = null;
      for (let i = 1; i < ordered.length; i++) {
        const prev = ordered[i - 1];
        const cur = ordered[i];
        if ((prev.version || '') !== (cur.version || '')) continue;
        const ps = (prev.status || '').toLowerCase();
        const cs = (cur.status || '').toLowerCase();
        const prevFailed = /fail|cancel/.test(ps);
        const curSuccess = /success/.test(cs);
        if (prevFailed && curSuccess) {
          flipCount++;
          lastFlipAt = cur.dateTimeStamp || cur.updatedAt || null;
        }
      }
      if (flipCount > 0) flaky.push({ appName, flipCount, lastFlipAt, sample: ordered[ordered.length - 1] });
    });
    return flaky.sort((a, b) => b.flipCount - a.flipCount).slice(0, 8);
  }, [allBuilds]);


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a202c', mb: 0.5 }}>
              Build Analytics Dashboard
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Real-time insights into build performance, trends, and diagnostics
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button data-tour="analytics-refresh" variant="contained" startIcon={<AutorenewRounded />} onClick={() => fetchDashboardData(true)} disabled={isLoadingAppData}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e6ed', boxShadow: 'none' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <BuildIcon color="primary" />
                  <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 700 }}>Total Builds</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>{buildStats.total}</Typography>
                <Typography variant="caption" color="textSecondary">Recent 1000 builds</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e6ed', boxShadow: 'none' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <CheckCircle color="success" />
                  <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 700 }}>Success</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#4caf50' }}>{buildStats.success}</Typography>
                <Typography variant="caption" color="textSecondary">{buildStats.total > 0 ? ((buildStats.success / buildStats.total) * 100).toFixed(1) : 0}% success rate</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e6ed', boxShadow: 'none' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 700 }}>Failed</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#f44336' }}>{buildStats.failed}</Typography>
                <Typography variant="caption" color="textSecondary">{buildStats.total > 0 ? ((buildStats.failed / buildStats.total) * 100).toFixed(1) : 0}% failure rate</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e6ed', boxShadow: 'none' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Cancel sx={{ color: '#ff9800' }} />
                  <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 700 }}>Canceled</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#ff9800' }}>{buildStats.canceled}</Typography>
                <Typography variant="caption" color="textSecondary">Manual interventions</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Row 1: Success/Failure Ratio & Daily Builds */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Success/Failure Ratio</Typography>
                <Typography variant="caption" color="textSecondary">
                  Distribution of build outcomes across all recent builds
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Bar data={{
                  labels: ['Success', 'Failed', 'Canceled', 'Running'],
                  datasets: [{
                    label: 'Builds',
                    data: [buildStats.success, buildStats.failed, buildStats.canceled, buildStats.running],
                    backgroundColor: ['#4caf50', '#f44336', '#ff9800', '#2196f3']
                  }]
                }} options={{
                  indexAxis: 'y',
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    datalabels: { color: '#fff', font: { weight: 'bold', size: 13 }, anchor: 'center', align: 'center' }
                  },
                  scales: {
                    x: { beginAtZero: true, ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                    y: { ticks: { color: '#1a202c', font: { weight: 'bold' } } }
                  }
                }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Builds Per Day (Last 7 Days)</Typography>
                <Typography variant="caption" color="textSecondary">
                  Daily build activity trend showing volume patterns
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Line data={{
                  labels: dailyBuilds.map(d => d.date),
                  datasets: [{
                    label: 'Builds',
                    data: dailyBuilds.map(d => d.count),
                    borderColor: '#2196f3',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    fill: true,
                    tension: 0.4
                  }]
                }} options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    datalabels: { color: '#1a202c', font: { weight: 'bold', size: 12 }, anchor: 'end', align: 'top' }
                  },
                  scales: {
                    x: { ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                    y: { beginAtZero: true, ticks: { color: '#1a202c', font: { weight: 'bold' } } }
                  }
                }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts Row 2: Phase Failures & User Activity */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: 450, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Phase Failure Analysis</Typography>
                <Typography variant="caption" color="textSecondary">
                  Identifies which build stages are failing most frequently
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                {phaseFailures.length > 0 ? (
                  <Bar data={{
                    labels: phaseFailures.map(p => p.phase),
                    datasets: [{
                      label: 'Failures',
                      data: phaseFailures.map(p => p.count),
                      backgroundColor: ['#ff6384', '#ff8a80', '#ff5252', '#f44336', '#e53935', '#d32f2f']
                    }]
                  }} options={{
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      datalabels: { color: '#fff', font: { weight: 'bold', size: 13 }, anchor: 'center', align: 'center' }
                    },
                    scales: {
                      x: { beginAtZero: true, ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                      y: { ticks: { color: '#1a202c', font: { weight: 'bold' } } }
                    }
                  }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="textSecondary">No phase failure data available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: 450, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Builds by User</Typography>
                <Typography variant="caption" color="textSecondary">
                  Top 10 users by build activity
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Doughnut data={{
                  labels: userBuilds.map(([user]) => user),
                  datasets: [{
                    data: userBuilds.map(([, count]) => count),
                    backgroundColor: [
                      '#FF6384', '#36A2EB', '#FFCE56', '#4BC192', '#9966FF',
                      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF9F40'
                    ]
                  }]
                }} options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        color: '#1a202c',
                        font: { size: 11, weight: 'bold' }
                      }
                    },
                    datalabels: { color: '#fff', font: { weight: 'bold', size: 13 }, anchor: 'center', align: 'center' }
                  }
                }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Chart Row 3: Top 10 Affected Apps Heatmap */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 4, borderRadius: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Top 10 Affected Apps Heatmap</Typography>
                <Typography variant="body2" color="textSecondary">
                  Applications with the highest failure and cancellation rates - prioritize these for investigation
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                {topFailedApps.length > 0 ? (
                  <Bar data={{
                    labels: topFailedApps.map(([app]) => app),
                    datasets: [{
                      label: 'Failures',
                      data: topFailedApps.map(([, count]) => count),
                      backgroundColor: topFailedApps.map((_, i) => {
                        const intensity = 1 - (i / topFailedApps.length) * 0.5;
                        return `rgba(244, 67, 54, ${intensity})`;
                      })
                    }]
                  }} options={{
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.parsed.x} failures`
                        }
                      },
                      datalabels: { color: '#fff', font: { weight: 'bold', size: 13 }, anchor: 'center', align: 'center' }
                    },
                    scales: {
                      x: { beginAtZero: true, grid: { display: false }, ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                      y: {
                        ticks: {
                          color: '#1a202c',
                          font: { size: 12, weight: 'bold' }
                        }
                      }
                    }
                  }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="textSecondary">No failure data available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* New Chart Row: Success-rate trend + Heatmap calendar */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 3, minHeight: 360, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TrendingUp sx={{ color: '#6366f1' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Success rate over time</Typography>
                </Stack>
                <Typography variant="caption" color="textSecondary">
                  Daily success rate across the last 30 days. Gaps indicate days with no builds.
                </Typography>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Line
                  data={{
                    labels: successRateTrend.map(d => d.date),
                    datasets: [{
                      label: 'Success rate (%)',
                      data: successRateTrend.map(d => d.rate),
                      borderColor: '#16a34a',
                      backgroundColor: 'rgba(22, 163, 74, 0.12)',
                      fill: true,
                      tension: 0.3,
                      spanGaps: true,
                      pointRadius: 3,
                    }],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const idx = ctx.dataIndex;
                            const total = successRateTrend[idx]?.total ?? 0;
                            return `${ctx.parsed.y?.toFixed(1) ?? '—'}% success · ${total} build${total !== 1 ? 's' : ''}`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: { min: 0, max: 100, ticks: { color: '#1a202c', font: { weight: 'bold' }, callback: (v) => `${v}%` } },
                      x: { ticks: { color: '#1a202c', font: { weight: 'bold' }, maxRotation: 0, autoSkip: true } },
                    },
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, minHeight: 360, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarMonth sx={{ color: '#6366f1' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{heatmapMonth.label}</Typography>
                </Stack>
                <Typography variant="caption" color="textSecondary">
                  Failure rate per day. Hover any cell for the exact split.
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <Typography key={`hdr-${i}`} variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 700 }}>{d}</Typography>
                ))}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, flex: 1 }}>
                {heatmapMonth.cells.map((cell, i) => {
                  if (!cell) return <Box key={`pad-${i}`} />;
                  // Days after today haven't happened yet — never colour them
                  // (they'd otherwise read as 0% failure = green).
                  const startOfTomorrow = new Date(); startOfTomorrow.setHours(0, 0, 0, 0); startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
                  const isFuture = cell.date >= startOfTomorrow;
                  const rate = isFuture ? null : cell.failureRate;
                  let bg = '#f1f5f9';
                  let color = '#94a3b8';
                  if (rate === null) { /* no builds / future — light grey */ }
                  else if (rate === 0) { bg = '#bbf7d0'; color = '#15803d'; }
                  else if (rate < 10) { bg = '#fef3c7'; color = '#92400e'; }
                  else if (rate < 25) { bg = '#fed7aa'; color = '#9a3412'; }
                  else if (rate < 50) { bg = '#fecaca'; color = '#991b1b'; }
                  else { bg = '#f87171'; color = '#fff'; }
                  const total = cell.bucket?.total ?? 0;
                  return (
                    <Tooltip
                      key={cell.key}
                      arrow
                      placement="top"
                      title={
                        rate === null
                          ? `${format(cell.date, 'MMM dd')}: no builds`
                          : `${format(cell.date, 'MMM dd')}: ${rate.toFixed(0)}% failure · ${total} build${total !== 1 ? 's' : ''}`
                      }
                    >
                      <Box
                        sx={{
                          minHeight: 32,
                          aspectRatio: '1 / 1',
                          maxHeight: 56,
                          backgroundColor: bg,
                          color,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          border: '1px solid rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        {cell.date.getDate()}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5, rowGap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Failure rate:</Typography>
                {[
                  { c: '#bbf7d0', l: '0%' },
                  { c: '#fef3c7', l: '<10%' },
                  { c: '#fed7aa', l: '<25%' },
                  { c: '#fecaca', l: '<50%' },
                  { c: '#f87171', l: '50%+' },
                ].map(item => (
                  <Box key={item.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: item.c, border: '1px solid rgba(0,0,0,0.05)' }} />
                    <Typography variant="caption" color="text.secondary">{item.l}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Flaky-build callout */}
        {flakyApps.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberRounded />}
            sx={{ mb: 3, borderRadius: 3, alignItems: 'flex-start' }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              {flakyApps.length} flaky app{flakyApps.length !== 1 ? 's' : ''} detected
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1, color: '#78350f' }}>
              These apps had a failed build followed by a success on the <strong>same version</strong> — usually a sign of a CI infrastructure issue rather than a code bug.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1, mt: 0.5 }}>
              {flakyApps.map(f => (
                <Chip
                  key={f.appName}
                  size="small"
                  label={`${f.appName} · ${f.flipCount}× flip`}
                  sx={{ fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#92400e' }}
                />
              ))}
            </Stack>
          </Alert>
        )}

        {/* Pointer to the Error Logs page for the table view that used to live here */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: '1px solid rgba(99, 102, 241, 0.25)',
            backgroundColor: 'rgba(99, 102, 241, 0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <ErrorIcon sx={{ color: '#6366f1' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a202c' }}>
                Looking for the detailed error table?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The searchable / groupable view with inline log preview now lives on the <strong>Error Logs</strong> page.
              </Typography>
            </Box>
          </Stack>
          <Button
            component={RouterLink}
            to="/home/error-logs"
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Open Error Logs
          </Button>
        </Paper>



        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default BuildAnalyticsDashboard;